import { anyGetter } from '../utils/accessors.js';
/**
 * A simple interface for handling the state of a data-driven tooltip
 */
export class TooltipState {
    #options;
    /**
     * The x position of the tooltip
     */
    x = $state(0);
    /**
     * The y position of the tooltip
     */
    y = $state(0);
    /**
     * Whether the tooltip is visible
     */
    visible = $state(false);
    /**
     * The data attached to the tooltip
     */
    data = $state(null);
    constructor(options) {
        this.#options = options;
    }
    hide() {
        this.visible = false;
    }
    show() {
        this.visible = true;
    }
    /**
     * Adjusts the position to [e.clientX, e.clientY]
     * @param e
     */
    mousemove(e) {
        let x = e.clientX;
        let y = e.clientY;
        if (this.#options?.offset) {
            [x, y] = this.#options.offset(x, y, 'pointer');
        }
        this.x = x;
        this.y = y;
    }
    mouseover(data) {
        this.visible = true;
        this.data = data;
    }
    focus(e, data) {
        const node = e.target;
        const bbox = node.getBoundingClientRect();
        let x = bbox.left;
        let y = bbox.top;
        if (this.#options?.offset) {
            [x, y] = this.#options.offset(x, y, 'keyboard');
        }
        this.x = x;
        this.y = y;
        this.visible = true;
        this.data = data;
    }
    keydown(e) {
        if (e.code === 'Escape') {
            this.hide();
        }
        else if (e.code === 'Space') {
            this.show();
        }
    }
    /**
     * Default selection props for the tooltip, designed to hook into a particular trigger
     */
    get selectionProps() {
        const mouseover = this.mouseover.bind(this);
        const mousemove = this.mousemove.bind(this);
        const focus = this.focus.bind(this);
        const keydown = this.keydown.bind(this);
        const hide = this.hide.bind(this);
        return function (record) {
            return {
                onmouseover: () => mouseover(record),
                onmousemove: mousemove,
                onmouseleave: hide,
                onfocus: (e) => focus(e, record),
                onblur: hide,
                onkeydown: keydown,
            };
        };
    }
    /**
     * Default selection props for the `KeyboardSelector` component
     */
    get keyboardProps() {
        return {
            onkeydown: (e) => {
                if (e.code === 'Escape') {
                    this.hide();
                }
                if (e.code === 'Space') {
                    this.show();
                }
            },
            onSelectionChange: (record) => {
                if (record === undefined || record === null) {
                    this.hide();
                    this.data = null;
                    return;
                }
                const asSelection = this.#options?.transformSelection
                    ? this.#options.transformSelection(record)
                    : record;
                this.mouseover(asSelection);
                if (this.#options?.x) {
                    const xVal = anyGetter(this.#options.x)(asSelection);
                    this.x = xVal.valueOf();
                }
                if (this.#options?.y) {
                    const yVal = anyGetter(this.#options.y)(asSelection);
                    this.y = yVal.valueOf();
                }
            },
        };
    }
    /**
     * Default movement/selection props for the `Pointer` component
     */
    get pointerProps() {
        return {
            onSelectionChange: (record) => {
                if (record === undefined) {
                    this.hide();
                    this.data = null;
                }
                else {
                    const asSelection = this.#options?.transformSelection
                        ? this.#options.transformSelection(record)
                        : record;
                    this.mouseover(asSelection);
                }
            },
            onMovement: (pointer) => {
                this.visible = pointer.isInside || pointer.current?.pointerType !== 'mouse';
                if (pointer.offset === undefined)
                    return;
                let [x, y] = pointer.offset;
                if (this.#options?.offset) {
                    [x, y] = this.#options.offset(x, y, 'pointer');
                }
                this.x = x;
                this.y = y;
            },
        };
    }
}
